import React, {
	useMemo,
	useEffect,
	useState,
	useCallback,
	useRef,
} from "react";
import VizSensor from "react-visibility-sensor";
import { motion } from "framer-motion";
import anim from "../../../utils/anim";
import useTrack, { eventTypes } from "../../../hooks/useTrack";
import GET from "../../../utils/GET";
import parseUtm from "../../../utils/parseUtm";
import getImgUrl from "../../../utils/getImgUrl";
import inFullscreen from "../../../utils/inFullscreen";
import { ROCKETS_BRAND_ID } from "../../../conf";
import accessibilityProvider from "../../../utils/accessibilityProvider";

const parsePrice = (price) => {
	return isNaN(price) ? price : Number(price).toFixed(2);
};

const inModal = GET("modal");
// const publisher = GET("publisher");

const ProductCard = React.memo(
	({
		product = {},
		order,
		ml_order,
		shopifyShop,
		modalVisible = false,
		populateCart = false,
		postPurchase,
		overrideUtm,
		utmFlags,
		isPremium,
		brandId,
		setPdpInfo,
		earnedCreditUsed = false,
		emailCreditUsed = false,
		brandMetadata = false,
		preferredDisplay = false,
		preferredStartDate = false,
	}) => {
		const track = useTrack();
		const [fired, setFired] = useState(false);
		const [trigger, setTrigger] = useState(false);
		const [displayed, setDisplayed] = useState(false);
		const productRef = useRef();

		const eventMetadata = useMemo(
			() => brandMetadata || {},
			[brandMetadata]
		);

		useEffect(() => {
			if (!fired && trigger && displayed && !modalVisible) {
				const { brand, name, id, price_str, discounted_price_str } =
					product;
				track(eventTypes.WIDGET_DISPLAY, {
					brand,
					product: name,
					ml_order,
					product_id: id,
					brand_view_order: order,
					premium_credit_used: isPremium,
					price: price_str || "",
					discounted_price: discounted_price_str || "",
					earned_credit_used: earnedCreditUsed,
					email_credit_used: emailCreditUsed,
					preferred_display: preferredDisplay,
					preferred_start_date: preferredStartDate,
					...eventMetadata,
				});
				setFired(true);
			}
		}, [
			product,
			track,
			displayed,
			fired,
			order,
			ml_order,
			modalVisible,
			isPremium,
			trigger,
			earnedCreditUsed,
			emailCreditUsed,
			eventMetadata,
			preferredDisplay,
			preferredStartDate,
		]);

		const triggerEvents = useCallback((msgData) => {
			if (!productRef.current) {
				return;
			}
			const scrollPos = msgData.value;
			const productOffset =
				msgData.iframe + productRef.current.getBoundingClientRect().y;

			if (scrollPos > productOffset + 10) {
				setTrigger(true);
			}
		}, []);

		const handleTrackMessage = useCallback(
			({ data: msgData }) => {
				if (
					msgData.type !== "scroll" ||
					(!postPurchase && msgData.iframe === 0)
				) {
					return;
				}
				triggerEvents(msgData);
			},
			[postPurchase, triggerEvents]
		);

		const handleScroll = useCallback(
			(e) => {
				const msgData = {
					value:
						document.documentElement.scrollTop + window.innerHeight,
					iframe: 0,
				};
				triggerEvents(msgData);
			},
			[triggerEvents]
		);

		useEffect(() => {
			if (!inModal) {
				return;
			}
			handleScroll();
			window.addEventListener("scroll", handleScroll);
			return () => window.removeEventListener("scroll", handleScroll);
		}, [handleScroll]);

		useEffect(() => {
			window.addEventListener("message", handleTrackMessage);
		}, [handleTrackMessage]);

		const handleClick = () => {
			const {
				name,
				id,
				brand,
				masked_url,
				price_str,
				discounted_price_str,
			} = product;
			let url = masked_url;

			// if (product.variants && shopifyShop && populateCart) {
			// 	url = `${shopifyShop}/cart/${
			// 		product.variants[0].shopify_variant_id
			// 	}:1`;
			// } else {
			// 	url = masked_url;
			// }

			const clickEventType = eventTypes.CLICK;
			const showCoopPDP =
				(product.variants &&
					shopifyShop &&
					populateCart &&
					setPdpInfo &&
					inFullscreen &&
					!GET("platform") &&
					typeof setPdpInfo === "function" &&
					postPurchase) ||
				false;

			track(clickEventType, {
				brand,
				product: name,
				product_id: id,
				brand_view_order: order,
				premium_credit_used: isPremium,
				ml_order,
				show_coop_pdp: showCoopPDP,
				price: price_str || "",
				discounted_price: discounted_price_str || "",
				earned_credit_used: earnedCreditUsed,
				email_credit_used: emailCreditUsed,
				preferred_display: preferredDisplay,
				preferred_start_date: preferredStartDate,
				...eventMetadata,
				// cost:
			});

			// console.log(product);
			if (showCoopPDP) {
				return setPdpInfo({ ...product, utmFlags, overrideUtm });
			}

			const productWindow = window.open();
			url = parseUtm(url, utmFlags, overrideUtm);
			if (!url) {
				url = "";
			}
			if (!productWindow) {
				window.location.href = url;
				return;
			}
			productWindow.opener = null;
			productWindow.location = url;
		};

		return (
			<VizSensor
				onChange={(isVisible) => {
					if (isVisible && !displayed) {
						setDisplayed(true);
					}
				}}
			>
				<motion.div
					className="widget-product-card"
					onClick={handleClick}
					variants={anim.card}
					initial="initial"
					animate="animate"
					ref={productRef}
					{...accessibilityProvider({
						name: `Product: ${product.name}`,
					})}
				>
					<img
						src={getImgUrl(product)}
						className="widget-product-image"
						alt={`${product.name}`}
					/>
					<div
						className={`widget-product-details ${
							!(product.price_str || product.discounted_price_str)
								? "widget-product-details-compact"
								: ""
						}`}
					>
						<div className={`widget-product-title`}>
							{product.name}
						</div>
						{brandId === ROCKETS_BRAND_ID ? (
							<div className="widget-product-price-container widget-product-extra-text">
								Up to 45% off
							</div>
						) : (
							<>
								{(product.price_str ||
									product.discounted_price_str) && (
									<div className="widget-product-price-container">
										{product.price_str && (
											<span className="widget-product-price-old">
												${parsePrice(product.price_str)}
											</span>
										)}
										<span className="widget-product-price-curr">
											$
											{parsePrice(
												product.discounted_price_str
											)}
										</span>
									</div>
								)}
							</>
						)}
					</div>
				</motion.div>
			</VizSensor>
		);
	}
);

export default ProductCard;
